import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import PageShell from '../components/chrome/PageShell';
import EventCard from '../components/EventCard';
import EmptyState from '../components/EmptyState';
import LoadingSpinner from '../components/LoadingSpinner';
import { categoryLabel } from '../constants/eventCategories';
import { eventService } from '../services';

/**
 * /events/in/:citySlug and /events/in/:citySlug/:category — the crawlable city
 * landing pages.
 *
 * These exist for search, so they are held to search's rules: a real <h1> that
 * matches the content, intro copy a human would read, internal links to the
 * sibling categories, and a hard 404 for a city or category we don't cover
 * (an empty 200 is a soft 404 and gets the whole pattern devalued).
 *
 * The route lives under /events/in/… rather than /events/:citySlug because
 * /events/:inviteCode already owns the two-segment shape — a city slug there
 * would be ambiguous with an invite code.
 */
const PAGE_SIZE = 24;

export default function EventsByCityPage() {
  const { citySlug, category } = useParams();
  const navigate = useNavigate();

  const [payload, setPayload] = useState(null);
  const [events, setEvents] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [notFound, setNotFound] = useState(false);

  // Reset when the city or category in the URL changes — otherwise switching
  // categories appends to the previous category's list.
  useEffect(() => {
    setPayload(null);
    setEvents([]);
    setPage(1);
    setLoading(true);
    setNotFound(false);
  }, [citySlug, category]);

  const load = useCallback(async (pageNum) => {
    const res = await eventService.getEventsByCity(citySlug, category || null, {
      page: pageNum,
      pageSize: PAGE_SIZE,
    });

    if (!res.success || !res.data) {
      // The backend 404s an unrecognised city or category; anything else is a
      // transient failure, and both land the visitor on the same honest page.
      if (pageNum === 1) setNotFound(true);
      return;
    }

    setPayload(res.data);
    setEvents((prev) => (pageNum === 1 ? res.data.events : [...prev, ...res.data.events]));
  }, [citySlug, category]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await load(1);
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [load]);

  const loadMore = async () => {
    if (loadingMore || !payload?.hasMore) return;
    setLoadingMore(true);
    const next = page + 1;
    await load(next);
    setPage(next);
    setLoadingMore(false);
  };

  const cityLabel = payload?.cityLabel || '';
  const catLabel = category ? categoryLabel(category) : null;
  const heading = catLabel ? `${catLabel} in ${cityLabel}` : `Events in ${cityLabel}`;

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (notFound) {
    return (
      <PageShell title="Nothing here" active="events">
        <EmptyState
          title="We don't cover that yet"
          description="That city or category isn't one we list. Try the events wall instead."
          actionLabel="SEE ALL EVENTS"
          onAction={() => navigate('/events/all')}
        />
      </PageShell>
    );
  }

  const siblings = (payload?.availableCategories || []).filter((c) => c.category !== category);
  const total = payload?.totalCount ?? 0;

  return (
    <PageShell
      title={heading}
      eyebrow={payload?.cityRegion || 'What’s on'}
      subtitle={
        total > 0
          ? `${total} upcoming ${total === 1 ? 'event' : 'events'} in and around ${cityLabel}.`
          : `Nothing on in ${cityLabel} right now.`
      }
      active="events"
      bare
    >
      <main className="mx-auto w-full max-w-content px-4 pb-20 pt-8 sm:px-8">
        {/* Intro copy — a landing page with nothing but a grid on it reads as a
            doorway page. Say what the page is, in plain words. */}
        <p className="mb-8 max-w-2xl font-mono text-sm leading-relaxed text-black">
          {catLabel
            ? `Every upcoming ${catLabel.toLowerCase()} event happening in and around ${cityLabel}, updated as new ones are announced. Tap any event for the full details, the venue, and tickets.`
            : `Everything happening in and around ${cityLabel} — gigs, dinners, comedy, trivia, and whatever else people are putting on. Tap any event for the full details, the venue, and tickets.`}
        </p>

        {/* Sibling categories. Only ever links to combinations that actually have
            events, so the nav can never point at an empty page. */}
        {siblings.length > 0 && (
          <nav aria-label="Browse by category" className="mb-8 border-2 border-black bg-white p-4">
            <h2 className="mb-3 font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-black">
              Browse {cityLabel} by category
            </h2>
            <ul className="flex flex-wrap gap-2">
              {category && (
                <li>
                  <Link
                    to={`/events/in/${citySlug}`}
                    className="block border-2 border-black px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-wider text-black transition-colors hover:bg-black hover:text-white"
                  >
                    All
                  </Link>
                </li>
              )}
              {siblings.map((c) => (
                <li key={c.category}>
                  <Link
                    to={`/events/in/${citySlug}/${c.category}`}
                    className="block border-2 border-black px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-wider text-black transition-colors hover:bg-black hover:text-white"
                  >
                    {categoryLabel(c.category)}
                    <span className="ml-1.5 font-normal text-gray-500">{c.count}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        )}

        {events.length === 0 ? (
          <EmptyState
            title={`Nothing on in ${cityLabel}`}
            description="No upcoming events here yet. Check the full wall, or be the one who starts something."
            actionLabel="SEE ALL EVENTS"
            onAction={() => navigate('/events/all')}
          />
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {events.map((e) => (
              <EventCard
                key={e.id}
                event={{
                  name: e.title,
                  date: e.dateTime,
                  location: e.venueName || e.locationCity,
                  posterUrl: e.posterUrl,
                  status: e.status,
                  category: e.category,
                  categories: e.categories,
                  customCategory: e.customCategory,
                  isExternalTicketing: e.isExternalTicketing,
                }}
                showProgress={false}
                onClick={() => navigate(`/events/${e.inviteCode}`)}
              />
            ))}
          </div>
        )}

        {payload?.hasMore && (
          <div className="mt-10 flex justify-center">
            <button
              type="button"
              onClick={loadMore}
              disabled={loadingMore}
              className="border-2 border-black bg-white px-6 py-3 font-mono text-xs font-bold uppercase tracking-wider text-black transition-colors hover:bg-black hover:text-white disabled:opacity-50"
            >
              {loadingMore ? 'Loading…' : 'Show more'}
            </button>
          </div>
        )}
      </main>
    </PageShell>
  );
}
